import React, { useState } from 'react';
import { ArrowLeft, Copy, Check, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const IphoneEmoji = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const iphoneEmojis = {
    smileys: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '🥲', '☺️', '😊'],
    hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔'],
    gestures: ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘'],
    faces: ['🥰', '😍', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜'],
    activities: ['🎮', '🎲', '🎭', '🎨', '🎤', '🎧', '🎼', '🎹', '🥁', '🎸']
  };

  const handleCopy = (emoji: string) => {
    navigator.clipboard.writeText(emoji);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are iPhone emojis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "iPhone emojis are Apple's implementation of Unicode emoji standards, known for their high-quality, detailed designs and regular updates with new expressions and symbols."
        }
      },
      {
        "@type": "Question",
        "name": "How do I use iPhone emojis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "iPhone emojis can be accessed through the emoji keyboard on iOS devices. They can be used in messages, social media, emails, and any text input that supports emoji characters."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>iPhone Emojis 📱 Complete Guide & Collection [{currentYear}] ✨</title>
        <meta name="description" content={`【COMPLETE GUIDE】 Access all iPhone emojis 📱 Latest designs & meanings ✨ Updated for ${currentYear} 🔄 Copy & paste iPhone-style emojis 💫 Perfect for all your messages!`} />
        <link rel="canonical" href="https://emojicollection.net/iphone-emojis" />
        
        <meta property="og:title" content={`iPhone Emojis 📱 Complete Guide & Collection [${currentYear}] ✨`} />
        <meta property="og:description" content={`Access all iPhone emojis 📱 Latest designs & meanings ✨ Updated for ${currentYear} 🔄 Copy & paste iPhone-style emojis!`} />
        <meta property="og:url" content="https://emojicollection.net/iphone-emojis" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`iPhone Emojis 📱 Complete Guide & Collection [${currentYear}] ✨`} />
        <meta name="twitter:description" content={`Access all iPhone emojis 📱 Latest designs & meanings ✨ Updated for ${currentYear} 🔄 Copy & paste iPhone-style emojis!`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="iphone emojis, apple emojis, ios emojis, iphone emoji keyboard, iphone emoji copy paste, apple emoji collection, ios emoji guide" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-500 mb-4">
                <Smartphone className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                iPhone Emojis 📱 Complete Guide & Collection [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Explore Apple's beautiful emoji collection - featuring high-quality designs, regular updates, and extensive emoji support. Updated for {currentYear} with the latest iOS emoji designs.
              </p>
            </header>

            {Object.entries(iphoneEmojis).map(([category, emojis]) => (
              <section key={category} className="mb-12">
                <h2 className="text-2xl font-semibold mb-6 capitalize">{category}</h2>
                <div className="grid grid-cols-5 md:grid-cols-10 gap-4">
                  {emojis.map((emoji, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="text-center mb-2">
                        <span className="text-2xl">{emoji}</span>
                      </div>
                      <button
                        onClick={() => handleCopy(emoji)}
                        className={`w-full inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium ${
                          copied ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'
                        } transition-colors`}
                      >
                        {copied ? (
                          <>
                            <Check className="mr-1" size={12} />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="mr-1" size={12} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <section className="prose prose-lg max-w-none">
              <h2>About iPhone Emojis</h2>
              <p>
                iPhone emojis are renowned for their high-quality design and regular updates. Apple's emoji implementation is known for:
              </p>
              <ul>
                <li>Detailed, expressive designs</li>
                <li>Regular updates with new emojis</li>
                <li>Consistent style across all symbols</li>
                <li>High-resolution graphics</li>
                <li>Universal compatibility</li>
              </ul>

              <h2>Latest Features</h2>
              <p>
                The latest iOS update brings several improvements to emojis:
              </p>
              <ul>
                <li>New emoji additions</li>
                <li>Enhanced design details</li>
                <li>Improved rendering</li>
                <li>Better cross-platform support</li>
                <li>Expanded emoji combinations</li>
              </ul>

              <h2>Using iPhone Emojis</h2>
              <p>
                iPhone emojis can be used in various ways:
              </p>
              <ul>
                <li>Messages and communication</li>
                <li>Social media posts</li>
                <li>Email signatures</li>
                <li>Digital content creation</li>
                <li>Personal expression</li>
              </ul>

              <h2>Platform Compatibility</h2>
              <p>
                While designed for iOS, iPhone emojis are widely supported:
              </p>
              <ul>
                <li>Full support on all Apple devices</li>
                <li>Compatible with major platforms</li>
                <li>Consistent display across iOS versions</li>
                <li>Web-friendly implementation</li>
              </ul>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What are iPhone emojis?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    iPhone emojis are Apple's implementation of Unicode emoji standards, known for their high-quality, detailed designs and regular updates with new expressions and symbols.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">How do I use iPhone emojis?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    iPhone emojis can be accessed through the emoji keyboard on iOS devices. They can be used in messages, social media, emails, and any text input that supports emoji characters.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Do iPhone emojis work everywhere?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    While the exact appearance may vary, the emojis will display on all major platforms and devices. The specific iPhone-style design will be seen on Apple devices, while other platforms will show their own versions.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default IphoneEmoji;